import { useState } from "react";
import type {
  EggSaleEntry,
  BirdSaleEntry,
  ManureSaleEntry,
  BagSaleEntry,
  SalesData,
} from "./DailyReportPage";
import { ADHOC_BUYER } from "./constants";
import NECCRateNotice from "@/components/sales/NECCRateNotice";
import SalePaymentSection from "@/components/sales/SalePaymentSection";
import type { PaymentDraft } from "@/hooks/useSalePayments";
import { inrWhole } from "@/lib/utils";
import { paidNow, saleTotal, takesPayment } from "./paidNow";
import type { Buyer, Flock } from "@/types/models";

interface Props {
  sales: SalesData;
  /** The report's date. The NECC benchmark is per day, so a backdated report
   *  must read against the rate declared on ITS day. */
  date: string;
  buyers: Buyer[];
  flocks: Flock[];
  onAddEgg: () => void;
  onUpdateEgg: (index: number, patch: Partial<EggSaleEntry>) => void;
  onRemoveEgg: (index: number) => void;
  onAddBird: () => void;
  onUpdateBird: (index: number, patch: Partial<BirdSaleEntry>) => void;
  onRemoveBird: (index: number) => void;
  onAddManure: () => void;
  onUpdateManure: (index: number, patch: Partial<ManureSaleEntry>) => void;
  onRemoveManure: (index: number) => void;
  onAddBag: () => void;
  onUpdateBag: (index: number, patch: Partial<BagSaleEntry>) => void;
  onRemoveBag: (index: number) => void;
}

const inputClass =
  "h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const selectClass = inputClass;

function buyersFor(buyers: Buyer[], category: string): Buyer[] {
  return buyers.filter(
    (b) => b.status === "active" && b.categories?.includes(category)
  );
}

export default function StepSales(props: Props) {
  const { sales, buyers, flocks } = props;
  // Default open when a section already has rows — so navigating Back into
  // this step re-opens whatever the operator was working on.
  const [open, setOpen] = useState<{ [k: string]: boolean }>(() => ({
    eggs: sales.eggs.length > 0,
    birds: sales.birds.length > 0,
    manure: sales.manure.length > 0,
    bags: sales.bags.length > 0,
  }));

  const toggle = (key: string) =>
    setOpen((o) => ({ ...o, [key]: !o[key] }));

  const eggBuyers = buyersFor(buyers, "eggs");
  const birdBuyers = buyersFor(buyers, "birds");
  const manureBuyers = buyersFor(buyers, "manure");
  const bagBuyers = buyersFor(buyers, "bags");

  // `flocks` is already filtered to active in DailyReportPage's useEffect.
  return (
    <div>
      <h4 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
        Sales (Optional)
      </h4>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Record any sales for the day. Leave a section empty if there's nothing
        to record there.
      </p>

      <Section
        title="Egg Sales"
        count={sales.eggs.length}
        isOpen={open.eggs}
        onToggle={() => toggle("eggs")}
      >
        {sales.eggs.length === 0 && (
          <EmptyRow message="No egg sales yet." />
        )}
        {sales.eggs.map((row, i) => (
          <EggRow
            key={i}
            row={row}
            date={props.date}
            buyers={eggBuyers}
            onChange={(patch) => props.onUpdateEgg(i, patch)}
            onRemove={() => props.onRemoveEgg(i)}
          />
        ))}
        <AddButton
          label="Add egg sale"
          onClick={props.onAddEgg}
          disabled={eggBuyers.length === 0}
          disabledHint={
            eggBuyers.length === 0
              ? "No buyers in the 'eggs' category. Ask owner to add one."
              : undefined
          }
        />
      </Section>

      <Section
        title="Bird Sales"
        count={sales.birds.length}
        isOpen={open.birds}
        onToggle={() => toggle("birds")}
      >
        {sales.birds.length === 0 && (
          <EmptyRow message="No bird sales yet." />
        )}
        {sales.birds.map((row, i) => (
          <BirdRow
            key={i}
            row={row}
            buyers={birdBuyers}
            flocks={flocks}
            onChange={(patch) => props.onUpdateBird(i, patch)}
            onRemove={() => props.onRemoveBird(i)}
          />
        ))}
        <AddButton
          label="Add bird sale"
          onClick={props.onAddBird}
          disabled={birdBuyers.length === 0}
          disabledHint={
            birdBuyers.length === 0
              ? "No buyers in the 'birds' category. Ask owner to add one."
              : undefined
          }
        />
      </Section>

      <Section
        title="Manure Sales"
        count={sales.manure.length}
        isOpen={open.manure}
        onToggle={() => toggle("manure")}
      >
        {sales.manure.length === 0 && (
          <EmptyRow message="No manure sales yet." />
        )}
        {sales.manure.map((row, i) => (
          <ManureRow
            key={i}
            row={row}
            buyers={manureBuyers}
            onChange={(patch) => props.onUpdateManure(i, patch)}
            onRemove={() => props.onRemoveManure(i)}
          />
        ))}
        <AddButton label="Add manure sale" onClick={props.onAddManure} />
      </Section>

      <Section
        title="Bag Sales"
        count={sales.bags.length}
        isOpen={open.bags}
        onToggle={() => toggle("bags")}
        last
      >
        {sales.bags.length === 0 && (
          <EmptyRow message="No bag sales yet." />
        )}
        {sales.bags.map((row, i) => (
          <BagRow
            key={i}
            row={row}
            buyers={bagBuyers}
            onChange={(patch) => props.onUpdateBag(i, patch)}
            onRemove={() => props.onRemoveBag(i)}
          />
        ))}
        <AddButton label="Add bag sale" onClick={props.onAddBag} />
      </Section>
    </div>
  );
}

// ---------- Section shell ----------

function Section({
  title,
  count,
  isOpen,
  onToggle,
  last,
  children,
}: {
  title: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`${last ? "" : "mb-3"} rounded-lg border border-gray-200 dark:border-gray-700`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-medium text-gray-800 dark:text-white/90">
          {title}
          {count > 0 && (
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-500/20 dark:text-brand-400">
              {count}
            </span>
          )}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {isOpen ? "Hide" : "Show"}
        </span>
      </button>
      {isOpen && (
        <div className="space-y-3 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
}

function EmptyRow({ message }: { message: string }) {
  return (
    <p className="text-xs text-gray-500 dark:text-gray-400">{message}</p>
  );
}

function AddButton({
  label,
  onClick,
  disabled,
  disabledHint,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledHint?: string;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent dark:border-gray-600 dark:text-brand-400 dark:hover:bg-brand-500/10"
      >
        + {label}
      </button>
      {disabledHint && (
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          {disabledHint}
        </p>
      )}
    </div>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-medium text-error-500 hover:text-error-600 dark:text-error-400"
    >
      Remove
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-gray-400">
        {label}
      </span>
      {children}
    </label>
  );
}

// ---------- Egg row ----------

function EggRow({
  row,
  date,
  buyers,
  onChange,
  onRemove,
}: {
  row: EggSaleEntry;
  date: string;
  buyers: Buyer[];
  onChange: (patch: Partial<EggSaleEntry>) => void;
  onRemove: () => void;
}) {
  const recalcTotal = (qty: string, price: string) => {
    const q = parseFloat(qty) || 0;
    const p = parseFloat(price) || 0;
    // Match the standalone sale pages — whole rupees, no decimals.
    return Math.round(q * p).toString();
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Buyer *">
          <select
            value={row.buyer_id}
            onChange={(e) => onChange({ buyer_id: e.target.value })}
            className={selectClass}
          >
            <option value="">Select buyer</option>
            {buyers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Qty (eggs) *">
          <input
            type="number"
            min={1}
            value={row.qty_eggs}
            onChange={(e) => {
              const qty = e.target.value;
              onChange({
                qty_eggs: qty,
                total_amount: recalcTotal(qty, row.price_per_egg),
              });
            }}
            className={inputClass}
          />
        </Field>
        <Field label="Price per egg *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.price_per_egg}
            onChange={(e) => {
              const price = e.target.value;
              onChange({
                price_per_egg: price,
                total_amount: recalcTotal(row.qty_eggs, price),
              });
            }}
            className={inputClass}
          />
          {/* A manager in daily_report_mode does their whole day here. The
              benchmark's argument is that it must be visible BEFORE a price
              is agreed — shipping it only on the standalone sale page would
              put it where the people at the till never look, which is the
              mistake the measurements form already made once. */}
          <NECCRateNotice date={date} pricePerEgg={row.price_per_egg} />
        </Field>
        <Field label="Total amount *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.total_amount}
            onChange={(e) => onChange({ total_amount: e.target.value })}
            className={inputClass}
          />
        </Field>
        <Field label="Broken eggs">
          <input
            type="number"
            min={0}
            value={row.broken_eggs}
            onChange={(e) => onChange({ broken_eggs: e.target.value })}
            className={inputClass}
          />
        </Field>
        <Field label="Notes">
          <input
            type="text"
            value={row.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            className={inputClass}
          />
        </Field>
      </div>
      <RowPayments row={row} onChange={onChange} />
      <div className="mt-2 text-right">
        <RemoveButton onClick={onRemove} />
      </div>
    </div>
  );
}

// What the buyer paid there and then (W2-6). Only for a buyer on the books: a
// walk-in's sale is already cash, and with no buyer there is no account to
// receive into. Nothing added means the whole sale is on credit, as before.
function RowPayments({
  row,
  onChange,
}: {
  row: { buyer_id: string; total_amount: string; payments?: PaymentDraft[] };
  onChange: (patch: { payments: PaymentDraft[] }) => void;
}) {
  if (!takesPayment(row)) return null;
  const pay = paidNow(row, (payments) => onChange({ payments }));
  return (
    <div className="mt-3">
      <SalePaymentSection pay={pay} invoiceTotal={saleTotal(row)} formatCurrency={inrWhole} />
    </div>
  );
}

// ---------- Bird row ----------

function BirdRow({
  row,
  buyers,
  flocks,
  onChange,
  onRemove,
}: {
  row: BirdSaleEntry;
  buyers: Buyer[];
  flocks: Flock[];
  onChange: (patch: Partial<BirdSaleEntry>) => void;
  onRemove: () => void;
}) {
  // The total fills itself from weight × rate per kg, or count × rate per
  // bird, as on the Bird Sales page and the wizard's other sale rows. It
  // didn't, and Next stayed greyed out until someone typed the sum (live walk
  // 2, W2-7). While a factor is missing the total is left as it is, so a total
  // typed by hand is not wiped.
  const withTotal = (patch: Partial<BirdSaleEntry>): Partial<BirdSaleEntry> => {
    const next = { ...row, ...patch };
    const rate = parseFloat(next.rate) || 0;
    const qty = next.rate_type === "per_kg"
      ? parseFloat(next.total_weight_kg) || 0
      : parseInt(next.bird_count) || 0;
    if (rate > 0 && qty > 0) {
      // Whole rupees, matching the Bird Sales page.
      return { ...patch, total_amount: Math.round(qty * rate).toString() };
    }
    return patch;
  };
  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Buyer *">
          <select
            value={row.buyer_id}
            onChange={(e) => onChange({ buyer_id: e.target.value })}
            className={selectClass}
          >
            <option value="">Select buyer</option>
            {buyers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Flock (optional)">
          <select
            value={row.flock_id}
            onChange={(e) => onChange({ flock_id: e.target.value })}
            className={selectClass}
          >
            <option value="">No flock (manual)</option>
            {flocks.map((f) => (
              <option key={f.id} value={f.id}>
                {f.batch_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Bird count *">
          <input
            type="number"
            min={1}
            value={row.bird_count}
            onChange={(e) => onChange(withTotal({ bird_count: e.target.value }))}
            className={inputClass}
          />
        </Field>
        <Field label="Total weight (kg)">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.total_weight_kg}
            onChange={(e) => onChange(withTotal({ total_weight_kg: e.target.value }))}
            className={inputClass}
          />
        </Field>
        <Field label="Rate type">
          <div className="flex gap-3 pt-2 text-xs">
            <label className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
              <input
                type="radio"
                checked={row.rate_type === "per_kg"}
                onChange={() => onChange(withTotal({ rate_type: "per_kg" }))}
              />
              Per kg
            </label>
            <label className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
              <input
                type="radio"
                checked={row.rate_type === "per_bird"}
                onChange={() => onChange(withTotal({ rate_type: "per_bird" }))}
              />
              Per bird
            </label>
          </div>
        </Field>
        <Field
          label={row.rate_type === "per_kg" ? "Rate per kg *" : "Rate per bird *"}
        >
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.rate}
            onChange={(e) => onChange(withTotal({ rate: e.target.value }))}
            className={inputClass}
          />
        </Field>
        <Field label="Total amount *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.total_amount}
            onChange={(e) => onChange({ total_amount: e.target.value })}
            className={inputClass}
          />
        </Field>
        <Field label="Notes">
          <input
            type="text"
            value={row.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            className={inputClass}
          />
        </Field>
      </div>
      <RowPayments row={row} onChange={onChange} />
      <div className="mt-2 text-right">
        <RemoveButton onClick={onRemove} />
      </div>
    </div>
  );
}

// ---------- Manure row ----------

const MANURE_UNITS = ["load", "kg", "tonne", "trolley"];

function ManureRow({
  row,
  buyers,
  onChange,
  onRemove,
}: {
  row: ManureSaleEntry;
  buyers: Buyer[];
  onChange: (patch: Partial<ManureSaleEntry>) => void;
  onRemove: () => void;
}) {
  const isAdhoc = row.buyer_id === ADHOC_BUYER;
  const recalcTotal = (qty: string, rate: string) => {
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    // Match the standalone sale pages — whole rupees, no decimals.
    return Math.round(q * r).toString();
  };
  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Buyer *">
          <select
            value={row.buyer_id}
            onChange={(e) =>
              onChange({
                buyer_id: e.target.value,
                buyer_adhoc: e.target.value === ADHOC_BUYER ? row.buyer_adhoc : "",
              })
            }
            className={selectClass}
          >
            <option value="">Select buyer</option>
            {buyers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
            <option value={ADHOC_BUYER}>Walk-in (one-time)</option>
          </select>
        </Field>
        {isAdhoc && (
          <Field label="Walk-in name *">
            <input
              type="text"
              value={row.buyer_adhoc}
              onChange={(e) => onChange({ buyer_adhoc: e.target.value })}
              className={inputClass}
            />
          </Field>
        )}
        <Field label="Quantity *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.qty}
            onChange={(e) => {
              const qty = e.target.value;
              onChange({
                qty,
                total_amount: recalcTotal(qty, row.rate_per_unit),
              });
            }}
            className={inputClass}
          />
        </Field>
        <Field label="Unit *">
          <select
            value={row.unit}
            onChange={(e) => onChange({ unit: e.target.value })}
            className={selectClass}
          >
            {MANURE_UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Rate per unit *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.rate_per_unit}
            onChange={(e) => {
              const rate = e.target.value;
              onChange({
                rate_per_unit: rate,
                total_amount: recalcTotal(row.qty, rate),
              });
            }}
            className={inputClass}
          />
        </Field>
        <Field label="Total amount *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.total_amount}
            onChange={(e) => onChange({ total_amount: e.target.value })}
            className={inputClass}
          />
        </Field>
        <Field label="Notes">
          <input
            type="text"
            value={row.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            className={inputClass}
          />
        </Field>
      </div>
      <RowPayments row={row} onChange={onChange} />
      <div className="mt-2 text-right">
        <RemoveButton onClick={onRemove} />
      </div>
    </div>
  );
}

// ---------- Bag row ----------

const BAG_TYPES = ["plastic", "gunny", "pp", "other"];

function BagRow({
  row,
  buyers,
  onChange,
  onRemove,
}: {
  row: BagSaleEntry;
  buyers: Buyer[];
  onChange: (patch: Partial<BagSaleEntry>) => void;
  onRemove: () => void;
}) {
  const isAdhoc = row.buyer_id === ADHOC_BUYER;
  const recalcTotal = (qty: string, rate: string) => {
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    // Match the standalone sale pages — whole rupees, no decimals.
    return Math.round(q * r).toString();
  };
  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Buyer *">
          <select
            value={row.buyer_id}
            onChange={(e) =>
              onChange({
                buyer_id: e.target.value,
                buyer_adhoc: e.target.value === ADHOC_BUYER ? row.buyer_adhoc : "",
              })
            }
            className={selectClass}
          >
            <option value="">Select buyer</option>
            {buyers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
            <option value={ADHOC_BUYER}>Walk-in (one-time)</option>
          </select>
        </Field>
        {isAdhoc && (
          <Field label="Walk-in name *">
            <input
              type="text"
              value={row.buyer_adhoc}
              onChange={(e) => onChange({ buyer_adhoc: e.target.value })}
              className={inputClass}
            />
          </Field>
        )}
        <Field label="Bag type *">
          <select
            value={row.bag_type}
            onChange={(e) => onChange({ bag_type: e.target.value })}
            className={selectClass}
          >
            {BAG_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Quantity *">
          <input
            type="number"
            min={1}
            value={row.qty}
            onChange={(e) => {
              const qty = e.target.value;
              onChange({
                qty,
                total_amount: recalcTotal(qty, row.rate_per_unit),
              });
            }}
            className={inputClass}
          />
        </Field>
        <Field label="Rate per unit *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.rate_per_unit}
            onChange={(e) => {
              const rate = e.target.value;
              onChange({
                rate_per_unit: rate,
                total_amount: recalcTotal(row.qty, rate),
              });
            }}
            className={inputClass}
          />
        </Field>
        <Field label="Total amount *">
          <input
            type="number"
            step="0.01"
            min={0}
            value={row.total_amount}
            onChange={(e) => onChange({ total_amount: e.target.value })}
            className={inputClass}
          />
        </Field>
        <Field label="Notes">
          <input
            type="text"
            value={row.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            className={inputClass}
          />
        </Field>
      </div>
      <RowPayments row={row} onChange={onChange} />
      <div className="mt-2 text-right">
        <RemoveButton onClick={onRemove} />
      </div>
    </div>
  );
}

